import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ReactFlow, MarkerType} from 'reactflow';

import getConfig from 'getConfig';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import EntityMapControls from 'registry/entity-map/EntityMapControls';
import EntityMapEdge from 'registry/entity-map/EntityMapEdge';
import EntityMapSchemaFilter from 'registry/entity-map/EntityMapSchemaFilter';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import {CenteredSpinner} from 'ui-library/Spinner';
import sortedBy from 'util/sortedBy';

import {DEFAULT_FIT_VIEW_SETTINGS} from './EntityMapConstants';
import {useEntityMapActorRef, useEntityMapSelector} from './EntityMapMachineProvider';
import {
  computeDisplayNodeCountsBySchema,
  getEdgesCappedByProductLimits,
  getNodesCappedByProductLimits,
  getSchemaFilteredEdges,
} from './EntityMapUtils';
import EntityNode from './EntityNode';

const ENTITY_MAP_NODE_TYPES = {
  entity: EntityNode,
};

const ENTITY_MAP_EDGE_TYPES = {
  entityEdge: EntityMapEdge,
};

const useStyles = createStyles(() => ({
  container: {
    display: 'flex',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  graphArea: {
    flex: 1,
    height: '100%',
    position: 'relative',
  },
  spinnerOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
}));

/**
 * Renders a view of the entity map for a given entityId
 */
export default function EntityMapView(): JSX.Element {
  const {classes} = useStyles();
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const isFilteringEnabled = getConfig('REGISTRY_ENABLE_ENTITY_MAP_FILTERING');
  const entityId = useEntityMapSelector((s) => s.context.entityId);
  const graphNodesById = useEntityMapSelector((s) => s.context.graphNodesById);
  const graphEdgesById = useEntityMapSelector((s) => s.context.graphEdgesById);
  const peakNodeCountBySchema = useEntityMapSelector((s) => s.context.peakNodeCountBySchema);
  const isRefetching = useEntityMapSelector((s) => s.matches('refetching'));
  const isIdle = useEntityMapSelector((s) => s.matches('idle'));
  const isReloading = useEntityMapSelector(
    (s) => s.matches('refetching') || s.matches('debouncingLayout') || s.matches('layouting')
  );
  const [isFilterReload, setIsFilterReload] = useState(false);
  const [graphKey, setGraphKey] = useState(0);
  useEffect(() => {
    if (isRefetching) {
      setIsFilterReload(true);
    } else if (isIdle && isFilterReload) {
      setIsFilterReload(false);
      setGraphKey((k) => k + 1);
    }
  }, [isRefetching, isIdle, isFilterReload]);
  const showThrobber = isFilterReload && isReloading;
  const actorRef = useEntityMapActorRef();

  const connectedSchemas = useEntityMapSelector((s) => s.context.connectedSchemas);

  const [hiddenSchemaIds, setHiddenSchemaIds] = useState<Set<string>>(new Set());

  // Builds the deduplicated schema list for the filter sidebar.
  // connectedSchemas may contain masked names (e.g. "Inaccessible Schema 1") for schemas
  // the user can't directly access. We resolve those to real names from loaded entity nodes.
  const filterSchemas = useMemo(() => {
    const realNameById = new Map<string, string>();
    for (const node of graphNodesById.values()) {
      if (!isInaccessible(node.data) && node.data.schemaName != null && node.data.schemaId != null) {
        realNameById.set(node.data.schemaId, node.data.schemaName);
      }
    }
    const includedSchemaIds = new Set<string>();
    const schemaList: Array<{id: string; name: string}> = [];
    for (const schema of connectedSchemas) {
      if (!includedSchemaIds.has(schema.id)) {
        includedSchemaIds.add(schema.id);
        schemaList.push({id: schema.id, name: realNameById.get(schema.id) ?? schema.name});
      }
    }
    const rootNode = graphNodesById.get(entityId);
    if (
      rootNode &&
      !isInaccessible(rootNode.data) &&
      rootNode.data.schemaName != null &&
      rootNode.data.schemaId != null &&
      !includedSchemaIds.has(rootNode.data.schemaId)
    ) {
      schemaList.push({id: rootNode.data.schemaId, name: rootNode.data.schemaName});
    }
    return sortedBy(schemaList, [(s) => s.name]);
  }, [connectedSchemas, graphNodesById, entityId]);

  const previousExcludedRef = useRef<string>('');

  const handleHiddenSchemaIdsChange = useCallback(
    (nextHiddenIds: Set<string>) => {
      setHiddenSchemaIds(nextHiddenIds);
      const excludedIds = sortedBy(Array.from(nextHiddenIds), [(id: string) => id]);
      const key = excludedIds.join(',');
      if (key !== previousExcludedRef.current) {
        previousExcludedRef.current = key;
        actorRef.send({type: 'updateExcludedSchemas', excludedSchemaIds: excludedIds});
      }
    },
    [actorRef]
  );

  const nodeIdsAfterSchemaFilters = useMemo(() => {
    if (hiddenSchemaIds.size === 0) {
      return null;
    }
    const ids = new Set<string>();
    ids.add(entityId);
    for (const node of graphNodesById.values()) {
      if (
        isInaccessible(node.data) ||
        node.data.schemaId == null ||
        !hiddenSchemaIds.has(node.data.schemaId)
      ) {
        ids.add(node.id);
      }
    }
    return ids;
  }, [graphNodesById, hiddenSchemaIds, entityId]);

  const {cappedEdges: displayEdges, edgesCapped} = useMemo(() => {
    const visibleEdges = getSchemaFilteredEdges(graphEdgesById, nodeIdsAfterSchemaFilters);
    return getEdgesCappedByProductLimits(visibleEdges, graphNodesById);
  }, [graphEdgesById, graphNodesById, nodeIdsAfterSchemaFilters]);

  const graphNodes = useMemo(() => {
    const nodes = Array.from(graphNodesById.values());
    if (!nodeIdsAfterSchemaFilters) {
      return nodes;
    }
    return nodes.filter((node) => nodeIdsAfterSchemaFilters.has(node.id));
  }, [graphNodesById, nodeIdsAfterSchemaFilters]);

  const displayNodes = useMemo(
    () => getNodesCappedByProductLimits(graphNodes, displayEdges, edgesCapped, entityId),
    [graphNodes, displayEdges, edgesCapped, entityId]
  );

  const displayNodeIds = useMemo(() => new Set(displayNodes.map((n) => n.id)), [displayNodes]);

  const finalEdges = useMemo(
    () => displayEdges.filter((e) => displayNodeIds.has(e.source) && displayNodeIds.has(e.target)),
    [displayEdges, displayNodeIds]
  );

  const nodeCountBySchema = useMemo(
    () =>
      computeDisplayNodeCountsBySchema(
        displayNodes,
        peakNodeCountBySchema,
        filterSchemas.map((s) => s.name)
      ),
    [displayNodes, peakNodeCountBySchema, filterSchemas]
  );

  return (
    <div className={classes.container}>
      {isFilteringEnabled && (
        <EntityMapSchemaFilter
          hiddenSchemaIds={hiddenSchemaIds}
          onChangeHiddenSchemaIds={handleHiddenSchemaIdsChange}
          schemas={filterSchemas}
          visibleNodeCount={displayNodes.length}
          totalNodeCount={graphNodesById.size}
          nodeCountBySchema={nodeCountBySchema}
        />
      )}
      <div className={classes.graphArea}>
        {showThrobber ? (
          <>
            <ReactFlow {...defaultReactFlowProps} nodes={[]} edges={[]}>
              <DefaultBackground />
            </ReactFlow>
            <div className={classes.spinnerOverlay}>
              <CenteredSpinner />
            </div>
          </>
        ) : (
          <ReactFlow
            key={graphKey}
            {...defaultReactFlowProps}
            defaultEdgeOptions={{
              style: {
                color: Color.GRAY_400,
              },
              markerEnd: {
                type: MarkerType.Arrow,
                width: 20,
                height: 20,
                color: Color.GRAY_400,
              },
              labelStyle: {
                color: Color.GRAY_800,
              },
            }}
            nodes={displayNodes}
            edges={finalEdges}
            nodeTypes={ENTITY_MAP_NODE_TYPES}
            edgeTypes={ENTITY_MAP_EDGE_TYPES}
            onlyRenderVisibleElements={true}
            fitView={true}
            fitViewOptions={DEFAULT_FIT_VIEW_SETTINGS}
            minZoom={0.1}
            deleteKeyCode={null}
            selectionKeyCode={null}
            nodesDraggable={false}
            onNodesChange={(changes) => {
              actorRef.send({type: 'onNodesChange', changes});
            }}
            onEdgesChange={(changes) => {
              actorRef.send({type: 'onEdgesChange', changes});
            }}
          >
            <DefaultBackground />
            <EntityMapControls />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
